import gql from "graphql-tag";

import {FetchPolicy, type Result} from "@khanacademy/wonder-blocks-data";
import type {Role} from "@khan/auth-shared";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {useLocation} from "@khan/urls";

import {getPhantomSelectedRole} from "#phantom-registration-package/phantom-selected-role.ts";

import type {
    useBypassRolePickerCurrentUserKAID,
    useBypassRolePickerCurrentUserKAIDVariables,
} from "./__graphql-types__/useBypassRolePickerCurrentUserKAID.ts";

type RolePickerBypassResult = {
    // Which role, if any, the user should be considered to have chosen.
    role: Role | undefined;
    // useBypassRolePicker loads some of the data it needs to make
    // a decision asynchronously. Status identifies whether the
    // decision is still pending or has resolved.
    status: Result<any>["status"];
};

/**
 * Indicates whether a phantom user has already selected a role and can
 * therefore bypass a role selection step in sign-up flows.
 *
 * For example, if the user told us their role through the
 * PhantomWelcomeBanner. If they later decide to create an account we
 * don't need to collect role again.
 *
 * Role selection bypass can be suppressed by adding the `skipRole`
 * query param to the URL with a value of `false`. For example, if a
 * user bypasses role selection but wants to go back and change their
 * role a "Back" link could include `?skipRole=false`.
 */
export default function useBypassRolePicker(): RolePickerBypassResult {
    const phantomSelectedRole = getPhantomSelectedRole();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const bypassParam = queryParams.get("skipRole");
    const isTeacherParam = queryParams.get("isteacher");
    const isParentParam = queryParams.get("isparent");

    const isTeacher =
        isTeacherParam === "1" || isTeacherParam?.toLowerCase() === "true";

    const isParent =
        isParentParam === "1" || isParentParam?.toLowerCase() === "true";

    const shouldSkipQuery =
        bypassParam?.toLowerCase() === "false" ||
        isTeacher ||
        isParent ||
        !phantomSelectedRole;

    const [kaidResult] = useQuery(CURRENT_USER_KAID_QUERY, {
        // By default, useQuery will only fetch from the network if the
        // data is not in the cache.
        //
        // Pre-phantom users do not have profiles. If you query a
        // pre-phantom for `query { user { <anything> } }` you get back
        // a `null` value for `user`. And this value gets cached.
        //
        // When our user first visited the site one or more queries
        // likely attempted to load a user profile and received a null
        // response establishing a cache entry.
        //
        // Profile does not get reloaded when a user transitions to
        // phantom.
        //
        // So, by default, unless the phantom user experienced a full
        // page load/reload after transitioning to phantom the cache may
        // contain a user but that user won't have a KAID.
        //
        // Instead, always make sure a new query is fired so the
        // phantom's KAID is guaranteed to become available.
        fetchPolicy: FetchPolicy.CacheAndNetwork,

        // Skip the KAID query entirely if the result will not be used.
        skip: shouldSkipQuery,
    });

    if (bypassParam?.toLowerCase() === "false") {
        return {
            role: undefined,
            status: "success",
        };
    }

    // if isteacher param is `true` or `1`, skip role picker and go to teacher flow
    if (isTeacher) {
        return {
            role: "teacher",
            status: "success",
        };
    }

    // if isparent param is `true` or `1`, skip role picker and go to parent flow
    if (isParent) {
        return {
            role: "parent",
            status: "success",
        };
    }

    if (!phantomSelectedRole) {
        return {
            role: undefined,
            status: "success",
        };
    }

    if (kaidResult.status !== "success") {
        return {
            role: undefined,
            status: kaidResult.status,
        };
    }

    const kaid = kaidResult.data.user?.kaid;

    if (!kaid || kaid !== phantomSelectedRole.kaid) {
        return {
            role: undefined,
            status: "success",
        };
    }

    const selectedRole = phantomSelectedRole.role;

    return {
        role: selectedRole,
        status: "success",
    };
}

export const CURRENT_USER_KAID_QUERY: GqlOperation<
    useBypassRolePickerCurrentUserKAID,
    useBypassRolePickerCurrentUserKAIDVariables
> = gqlOp(gql`
    query useBypassRolePickerCurrentUserKAID {
        user {
            id
            kaid
        }
    }
`);
